import { v1 as uuidV1 } from 'uuid';
import * as browserStorage from 'utils/browserStorage';
import { COOKIE_STORAGE, storageKeys } from 'utils/browserStorage/constants';
import PluginService from '../PluginService';
import loggerResolver from './LoggerResolver';
import { logLevel, transactions, transactionSeq } from './constants';

export function validateLoggerData(data) {
  if (typeof data !== 'object') {
    throw new Error('Invalid data shape, Please send in this format: {TODO}');
  }
}

class LoggerProvider {
  /**
   *
   * @param {string} transactionName
   * @param {object} transaction
   * @param {object} transactionParams
   */
  saveTransaction(transactionName, transaction, transactionParams) {
    const transactionParameters = transactionParams
      || JSON.parse(browserStorage.load(storageKeys.TRANSACTION_PARAMS, COOKIE_STORAGE) || '{}');

    browserStorage.save(
      storageKeys.TRANSACTION_PARAMS,
      JSON.stringify({ ...transactionParameters, [transactionName]: transaction }),
      COOKIE_STORAGE
    );
  }

  /**
   *
   * @param {string} transactionName
   * @returns transactionId UUID
   */
  initTransaction(transactionName) {
    const transactionId = uuidV1();
    const transaction = { id: transactionId };

    this.saveTransaction(transactionName, transaction);
    return transactionId;
  }

  /**
   *
   * @param {string} transactionName
   * @returns transactionId UUID
   */
  finishTransaction(transactionName) {
    const transactionParams = JSON.parse(browserStorage.load(storageKeys.TRANSACTION_PARAMS, COOKIE_STORAGE) || '{}');
    const transaction = transactionParams[transactionName] || {};

    browserStorage.save(
      storageKeys.TRANSACTION_PARAMS,
      JSON.stringify({ ...transactionParams, [transactionName]: undefined }),
      COOKIE_STORAGE
    );
    return transaction.id;
  }

  /**
   *
   * @param {string} transactionName
   * @param {string} segmentName
   * @returns segmentId UUID
   */
  continueTransaction(transactionName, segmentName) {
    const transactionParams = JSON.parse(browserStorage.load(storageKeys.TRANSACTION_PARAMS, COOKIE_STORAGE) || '{}');
    const transaction = transactionParams[transactionName];
    if (!transaction) {
      return { segmentId: undefined, transactionId: undefined };
    }
    const segmentId = transaction[segmentName] || uuidV1();

    const updatedTransaction = { ...transaction, [segmentName]: segmentId };

    this.saveTransaction(transactionName, updatedTransaction);
    return { segmentId, transactionId: transaction.id };
  }

  getTransactionArgs(data) {
    let transactionId;
    let segmentId;
    const { transactionName, transactionSequence, segmentName } = data;

    if (transactions.includes(transactionName) && transactionSequence === transactionSeq.START) {
      transactionId = this.initTransaction(transactionName);
    }

    if (transactions.includes(transactionName) && transactionSequence === transactionSeq.STOP) {
      transactionId = this.finishTransaction(transactionName);
    }

    if (transactions.includes(transactionName) && segmentName) {
      const { transactionId: tId, segmentId: sId } = this.continueTransaction(transactionName, segmentName);
      transactionId = tId;
      segmentId = sId;
    }

    return {
      eventId: uuidV1(),
      segmentId,
      segmentName,
      transactionId,
      transactionName,
      transactionSequence,
    };
  }

  async getLearnerArgs(level, target, data) {
    const { id: elementId, type: elementType, deploymentId, cohortId } = target;
    const plugin = PluginService.build({ id: this.id });
    const { id: pluginId, version } = await plugin.getPlugin(elementId);
    const { message, args = {} } = data;

    const transactionArgs = this.getTransactionArgs(data);
    const {
      eventId, segmentId, segmentName, transactionId, transactionName, transactionSequence,
    } = transactionArgs;

    return {
      args: JSON.stringify({ ...args }),
      cohortId,
      deploymentId,
      elementId,
      elementType,
      level,
      message,
      pluginContext: 'LEARNER',
      pluginId,
      eventId,
      segmentId,
      segmentName,
      transactionId,
      transactionName,
      transactionSequence,
      version,
    };
  }

  logPluginLogs(level, target, data) {
    validateLoggerData(data);
    this.getLearnerArgs(level, target, data).then(
      (arg) => loggerResolver.learnerPluginLog(arg)
    );
  }

  debug(target, data) {
    this.logPluginLogs(logLevel.DEBUG, target, data);
  }

  error(target, data) {
    this.logPluginLogs(logLevel.ERROR, target, data);
  }

  info(target, data) {
    this.logPluginLogs(logLevel.INFO, target, data);
  }

  warn(target, data) {
    this.logPluginLogs(logLevel.WARN, target, data);
  }
}

export default new LoggerProvider();
